import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { fetchTenantDetail } from "../../actions/adminActions";

import TextGrid from "../TextGrid";
import {
	setSubheaderText,
	setSubheaderActions,
} from "../../actions/navigateActions";

import MaterialTabs from "../MaterialTabs";
import { Pill } from "../Tables/CellWithPills";
import { firmStatusPillConfig, getFirmStatusPillLabel } from "./firmStatusPill";

import AdminUsersForTenant from "./AdminUsersForTenant";
import AdminSearchIndexManagement from "./AdminSearchIndexManagement";
import AdminBatchJobs from "./AdminBatchJobs";
import AdminRecordCounts from "./AdminRecordCounts";
import FullSubpageError from "../Loading/FullSubpageError";

const tabMap = [
	{ label: "Users", url: "users" },
	{ label: "Search Index", url: "searchindex" },
	{ label: "Batch Jobs", url: "batchjobs" },
	{ label: "Records", url: "records" },
];

const subpageMap = {
	users: 0,
	searchindex: 1,
	batchjobs: 2,
	records: 3,
};

const renderSearchIndexStatus = (searchIndex) => {
	if (!searchIndex) {
		return "Loading...";
	}

	if (searchIndex.docsCount >= 0) {
		return <span>{searchIndex.docsCount.toLocaleString()} records</span>;
	} else if (searchIndex.docsCount === -1) {
		return (
			<div
				className="tagPill"
				style={{
					color: "white",
					backgroundColor: "var(--lw-darker-red)",
					fontSize: 16,
				}}
				title="Search index does not exist for this tenant"
			>
				No Index
			</div>
		);
	} else {
		return (
			<div
				className="tagPill"
				style={{
					color: "white",
					backgroundColor: "var(--lw-darker-red)",
					fontSize: 16,
				}}
				title="Could not connect to OpenSearch"
			>
				Could Not Connect
			</div>
		);
	}
};

function TenantDetail() {
	const { tenantID, tenantSubpage } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [tenant, setTenant] = useState({});
	const [searchIndex, setSearchIndex] = useState(null);
	const [fullTenantDetail, setFullTenantDetail] = useState(null);
	const [error, setError] = useState(null);
	const [refreshTick, setRefreshTick] = useState(0);

	const loadTenantDetail = useCallback(() => {
		dispatch(fetchTenantDetail(tenantID))
			.then((res) => {
				setTenant(res.mysql.tenant);
				setSearchIndex(res.mysql.searchIndex);
				setFullTenantDetail(res);
				setError(null);
				const pillLabel = getFirmStatusPillLabel(
					res.mysql.tenant.planType
				);
				dispatch(
					setSubheaderText({
						subheaderText: pillLabel ? (
							<>
								{res.mysql.tenant.name}
								<Pill
									label={pillLabel}
									style={{
										...firmStatusPillConfig[pillLabel]
											.style,
										verticalAlign: "middle",
										fontSize: 16,
									}}
									title={
										firmStatusPillConfig[pillLabel].title
									}
								/>
							</>
						) : (
							res.mysql.tenant.name
						),
						subheaderIconName: `${res.mysql.tenantRegion}_FLAG`,
					})
				);
			})
			.catch((err) => {
				console.error(err);
				if (
					err &&
					err.lxwError &&
					(err.lxwErrorCode === 5014 || err.lxwErrorCode === 5012)
				) {
					setError(err.lxwError);
				}
			});
	}, [tenantID, dispatch]);

	useEffect(() => {
		dispatch(setSubheaderText({ subheaderText: "Tenant Details" }));
	}, [dispatch]);

	useEffect(() => {
		loadTenantDetail();
	}, [loadTenantDetail]);

	const handleHeaderRefresh = useCallback(() => {
		loadTenantDetail();
		setRefreshTick((tick) => tick + 1);
	}, [loadTenantDetail]);

	useEffect(() => {
		dispatch(
			setSubheaderActions(
				<Button
					variant="default"
					size="xs"
					leftSection={<IconRefresh size={14} />}
					onClick={handleHeaderRefresh}
				>
					Refresh
				</Button>
			)
		);
		return () => dispatch(setSubheaderActions(null));
		// fullTenantDetail is a dep so these actions get re-set after each
		// loadTenantDetail() call, since setSubheaderText() wipes subheaderActions
	}, [dispatch, handleHeaderRefresh, fullTenantDetail]);

	const switchedTab = useCallback(
		(value) => {
			const newURL = `/admin/tenant/${tenantID}/${tabMap[value].url}`;
			navigate(newURL);
		},
		[tenantID, navigate]
	);

	const getSelectedTabContent = () => {
		if (tenantSubpage === "users") {
			return (
				<AdminUsersForTenant
					tenantID={tenantID}
					tenantDetail={fullTenantDetail}
				/>
			);
		} else if (tenantSubpage === "batchjobs") {
			return (
				<AdminBatchJobs
					tenantID={tenantID}
					tenantName={tenant.name}
					refreshTick={refreshTick}
				/>
			);
		} else if (tenantSubpage === "searchindex") {
			return (
				<AdminSearchIndexManagement
					tenantID={tenant.id}
					tenantName={tenant.name}
					searchIndexDetail={searchIndex}
				/>
			);
		} else if (tenantSubpage === "records") {
			return (
				<AdminRecordCounts
					tenantID={tenantID}
					refreshTick={refreshTick}
				/>
			);
		}
	};

	if (error) {
		return (
			<FullSubpageError
				title="Tenant Not Found"
				errorCode={error.code}
				description={error.description}
				userMessage={error.userMessage}
			/>
		);
	}

	return (
		<div className="fullHeightFlex noDefaultMargin">
			<div
				className="clientInfoHeader"
				style={{
					position: "relative",
					padding: 16,
				}}
			>
				<TextGrid
					numColumns={3}
					columns={[
						["TenantID", tenant.id],
						["Internal Customer ID", tenant.customerID],
						["Username Prefix", tenant.usernamePrefix],
					]}
				/>
				<TextGrid
					numColumns={3}
					columns={[
						["Search Index", renderSearchIndexStatus(searchIndex)],
					]}
				/>

				<div style={{ position: "absolute", top: 12, right: 12 }}>
					{}
				</div>
			</div>
			<MaterialTabs
				activeTab={subpageMap[tenantSubpage]}
				tabs={tabMap}
				switchedTab={switchedTab}
			/>
			{getSelectedTabContent()}
		</div>
	);
}

export default TenantDetail;
